import { useMemo } from "react";
import { UseTranslateResult } from "@tolgee/react";
import { useTimerStore } from "store/timer/time.store";
import { Currency, IStage, SurchargeType } from "@evennt-tech/api-v2-core";
import { sortStages } from "view/apps/Supplier/pages/shared/SurchargeModal";
import { currencySymbols } from "constraints/general";
import { calculateProductPriceWithFees } from "hooks/helpers";

type TFn = UseTranslateResult["t"];

interface RemainingTime {
  totalMs: number;
  formatted: string;
  nextStage?: IStage;
}

type TimeBreakdown =
  | { kind: "lt_min" }
  | { kind: "minutes"; minutes: number }
  | { kind: "hours"; hours: number }
  | { kind: "hours_minutes"; hours: number; minutes: number }
  | { kind: "days"; days: number }
  | { kind: "days_hours"; days: number; hours: number }
  | { kind: "months"; months: number }
  | { kind: "months_days"; months: number; days: number };

type RawRemaining =
  | { isEmpty: true }
  | {
      isEmpty: false;
      totalMs: number;
      nextStage: IStage;
      price: string;
      time: TimeBreakdown;
    };

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;

const COUNTDOWN_KEY = "common.shopping_page.surcharge_countdown";

const formatTimeBreakdown = (time: TimeBreakdown, t?: TFn): string => {
  switch (time.kind) {
    case "lt_min":
      return t
        ? t({ key: `${COUNTDOWN_KEY}.less_than_min`, defaultValue: "<1 min" })
        : "<1 min";
    case "minutes":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.minutes`,
            params: { minutes: time.minutes },
            defaultValue: "{minutes}min",
          })
        : `${time.minutes}min`;
    case "hours":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.hours`,
            params: { hours: time.hours },
            defaultValue: "{hours}h",
          })
        : `${time.hours}h`;
    case "hours_minutes":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.hours_minutes`,
            params: { hours: time.hours, minutes: time.minutes },
            defaultValue: "{hours}h {minutes}min",
          })
        : `${time.hours}h ${time.minutes}min`;
    case "days":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.days`,
            params: { days: time.days },
            defaultValue: "{days, plural, one {# Day} other {# Days}}",
          })
        : `${time.days} Days`;
    case "days_hours":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.days_hours`,
            params: { days: time.days, hours: time.hours },
            defaultValue: "{days, plural, one {# Day} other {# Days}} {hours}h",
          })
        : `${time.days} Days ${time.hours}h`;
    case "months":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.months`,
            params: { months: time.months },
            defaultValue: "{months, plural, one {# Month} other {# Months}}",
          })
        : `${time.months} Months`;
    case "months_days":
      return t
        ? t({
            key: `${COUNTDOWN_KEY}.months_days`,
            params: { months: time.months, days: time.days },
            defaultValue:
              "{months, plural, one {# Month} other {# Months}} {days, plural, one {# Day} other {# Days}}",
          })
        : `${time.months} Months ${time.days} Days`;
    default:
      return "";
  }
};

type UseRemainingTimeArgs = {
  surchargeStages?: IStage[];
  deliveryStartMS: number;
  basePrice: number;
  organiserFee: number;
  supplierFee: number;
  currency: Currency;
  t?: TFn;
};

const emptyState: RemainingTime = {
  totalMs: 0,
  formatted: "",
};

export function useRemainingTime({
  surchargeStages,
  deliveryStartMS,
  basePrice,
  organiserFee,
  supplierFee,
  currency,
  t,
}: UseRemainingTimeArgs): RemainingTime {
  const nowMS = useTimerStore((state) => state.now);

  const raw = useMemo((): RawRemaining => {
    if (!surchargeStages?.length) {
      return { isEmpty: true };
    }

    let stageIndex = -1;

    const sortedStages = surchargeStages.sort(sortStages);
    sortedStages.forEach((surchargeStage, index) => {
      const minDateOfSurchargeMS =
        deliveryStartMS - surchargeStage.daysBeforeBuild * DAY_MS;
      if (minDateOfSurchargeMS <= nowMS) {
        stageIndex = index;
      }
    });

    if (stageIndex >= sortedStages.length) {
      return { isEmpty: true };
    }

    const nextStage = sortedStages[stageIndex + 1];

    if (!nextStage || nextStage.type === SurchargeType.unpublish) {
      return { isEmpty: true };
    }

    const surchargeDateMS =
      deliveryStartMS - nextStage.daysBeforeBuild * DAY_MS;

    const totalMs = surchargeDateMS - nowMS;

    if (totalMs < 0) {
      return { isEmpty: true };
    }

    let time: TimeBreakdown;
    if (totalMs < MINUTE_MS) {
      time = { kind: "lt_min" };
    } else if (totalMs < HOUR_MS) {
      time = { kind: "minutes", minutes: Math.floor(totalMs / MINUTE_MS) };
    } else if (totalMs < DAY_MS) {
      const hours = Math.floor(totalMs / HOUR_MS);
      const minutes = Math.floor((totalMs % HOUR_MS) / MINUTE_MS);
      time =
        minutes > 0
          ? { kind: "hours_minutes", hours, minutes }
          : { kind: "hours", hours };
    } else if (totalMs < MONTH_MS) {
      const days = Math.floor(totalMs / DAY_MS);
      const hours = Math.floor((totalMs % DAY_MS) / HOUR_MS);
      time =
        hours > 0
          ? { kind: "days_hours", days, hours }
          : { kind: "days", days };
    } else {
      const months = Math.floor(totalMs / MONTH_MS);
      const days = Math.floor((totalMs % MONTH_MS) / DAY_MS);
      time =
        days > 0
          ? { kind: "months_days", months, days }
          : { kind: "months", months };
    }

    const surchargeAmount =
      nextStage.type === SurchargeType.percentage
        ? (basePrice / 100) * (Number(nextStage.amount) || 0)
        : Number(nextStage.amount) || 0;

    const nextPrice = basePrice + surchargeAmount;
    const nextPriceWithFees = calculateProductPriceWithFees(
      nextPrice,
      organiserFee,
      supplierFee
    );

    const price = `${currencySymbols[currency]}${nextPriceWithFees.toFixed(2)}`;

    return { isEmpty: false, totalMs, nextStage, price, time };
  }, [
    nowMS,
    deliveryStartMS,
    surchargeStages,
    basePrice,
    organiserFee,
    supplierFee,
    currency,
  ]);

  if (raw.isEmpty) {
    return emptyState;
  }

  const time = formatTimeBreakdown(raw.time, t);
  const formatted = t
    ? t({
        key: `${COUNTDOWN_KEY}.price_in_time`,
        params: { price: raw.price, time },
        defaultValue: "{price} In {time}",
      })
    : `${raw.price} In ${time}`;

  return { totalMs: raw.totalMs, formatted, nextStage: raw.nextStage };
}
